import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ChevronDown } from './Icons';
import { externalStatsService } from '../../services/externalStatsService';
import SearchModal from './SearchModal';

/**
 * Reusable player search bar with two modes:
 *  - "tracker"  → Live Tracker: external in-game stats by platform + gamertag,
 *                 same flow as the /stats page → navigates to /stats/:playerId.
 *  - "platform" → Platform search: the app's own players / teams / tournaments,
 *                 same as the navbar search (opens the SearchModal).
 *
 * `size="hero"` renders the large, homepage-hero variant.
 */
const PLATFORMS = [
  { id: 'steam', name: 'Steam', icon: '/steam.ico' },
  { id: 'psn', name: 'PlayStation', icon: '/psn.ico' },
  { id: 'xbox', name: 'Xbox', icon: '/xbox.ico' }
];

const PlayerSearchBar = ({ size = 'default', autoFocus = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mode, setMode] = useState('tracker'); // 'tracker' | 'platform'
  const [query, setQuery] = useState('');
  const [platform, setPlatform] = useState('steam');
  const [loading, setLoading] = useState(false);
  const [showPlatforms, setShowPlatforms] = useState(false);
  const [siteModalOpen, setSiteModalOpen] = useState(false);
  const platformRef = useRef(null);

  const isHero = size === 'hero';
  const isTracker = mode === 'tracker';

  useEffect(() => {
    const onClickOutside = (e) => {
      if (platformRef.current && !platformRef.current.contains(e.target)) {
        setShowPlatforms(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Persist to the same store the /stats page reads (localStorage key + shape).
  const addToRecentSearches = (plat, identifier, playerData) => {
    try {
      const p = plat.toLowerCase();
      const entry = {
        id: `${p}_${identifier}`,
        platform: p,
        identifier,
        displayName: playerData?.player?.displayName || playerData?.player?.username || identifier,
        avatar_url: playerData?.player?.avatarUrl,
        avatarUrl: playerData?.player?.avatarUrl,
        username: playerData?.player?.username || playerData?.player?.displayName || identifier,
        timestamp: new Date().toISOString()
      };
      const prev = JSON.parse(localStorage.getItem('officialStatsRecentSearches') || '[]');
      const filtered = prev.filter(s => !(s.platform === p && String(s.identifier).toLowerCase() === String(identifier).toLowerCase()));
      localStorage.setItem('officialStatsRecentSearches', JSON.stringify([entry, ...filtered].slice(0, 5)));
    } catch (_) { /* localStorage unavailable — non-fatal */ }
  };

  const handleSearch = async () => {
    if (loading) return;

    // Platform mode → hand off to the site-wide search (players/teams/tournaments).
    // Opens even when empty, matching the navbar search button.
    if (!isTracker) {
      setSiteModalOpen(true);
      return;
    }

    const identifier = query.trim();
    if (!identifier) return;

    setLoading(true);
    try {
      const result = await externalStatsService.getPlayerStats(platform, identifier);
      if (result?.success && result?.data) {
        const transformed = externalStatsService.transformForUI(result.data);
        const actualId = result.data.player?.platform_id || identifier;
        if (transformed?.player) {
          addToRecentSearches(platform, actualId, transformed);
          navigate(`/stats/${platform}_${actualId}`);
          return;
        }
      }
      // Fallback: navigate with the raw identifier
      navigate(`/stats/${platform}_${identifier}`);
    } catch (error) {
      navigate(`/stats/${platform}_${identifier}`);
    } finally {
      setLoading(false);
    }
  };

  const selectedPlatform = PLATFORMS.find((p) => p.id === platform);

  const modes = [
    { id: 'tracker', label: t('home.searchMode.tracker', { defaultValue: 'Live Tracker' }) },
    { id: 'platform', label: t('home.searchMode.platform', { defaultValue: 'Platform' }) }
  ];

  return (
    <div className={`w-full ${isHero ? 'max-w-2xl' : 'max-w-md'} mx-auto`}>
      {/* Mode switch: in-game stats (Live Tracker) vs on-platform search */}
      <div className="flex justify-center mb-3">
        <div className="inline-flex items-center gap-0.5 p-1 rounded-full border border-white/10 bg-zinc-900/60 backdrop-blur-md">
          {modes.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => { setMode(m.id); setShowPlatforms(false); }}
              className={`px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 ${
                mode === m.id
                  ? 'bg-[#31C590]/20 text-[#23F8B5] shadow-[inset_0_0_0_1px_rgba(35,248,181,0.35)]'
                  : 'text-white/60 hover:text-white'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className={`flex items-stretch gap-2 rounded-2xl border border-white/10 bg-zinc-900/70 backdrop-blur-md shadow-2xl ${
          isHero ? 'p-2' : 'p-1.5'
        } focus-within:border-[#31C590]/60 focus-within:shadow-[0_0_30px_rgba(49,197,144,0.25)] transition-all duration-300`}
      >
        {/* Platform selector — Live Tracker only */}
        {isTracker && (
        <div className="relative flex-shrink-0" ref={platformRef}>
          <button
            type="button"
            onClick={() => setShowPlatforms((v) => !v)}
            className={`flex items-center gap-2 h-full rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200 transition-colors ${
              isHero ? 'px-4 text-base' : 'px-3 text-sm'
            }`}
          >
            <img src={selectedPlatform?.icon} alt={selectedPlatform?.name} className="w-5 h-5" />
            <span className="hidden sm:inline">{selectedPlatform?.name}</span>
            <ChevronDown className="w-4 h-4 opacity-60" />
          </button>
          {showPlatforms && (
            <div className="absolute z-20 mt-2 w-44 rounded-xl border border-white/10 bg-zinc-900/95 backdrop-blur-md shadow-2xl overflow-hidden">
              {PLATFORMS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => {
                    setPlatform(p.id);
                    setShowPlatforms(false);
                  }}
                  className={`flex items-center gap-2 w-full px-4 py-2.5 text-sm text-gray-200 hover:bg-white/10 transition-colors ${
                    platform === p.id ? 'bg-white/5' : ''
                  }`}
                >
                  <img src={p.icon} alt={p.name} className="w-5 h-5" />
                  <span>{p.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Input */}
        <div className="flex items-center flex-1 min-w-0">
          <Search className={`flex-shrink-0 text-gray-500 ${isHero ? 'w-5 h-5 ml-2' : 'w-4 h-4 ml-1.5'}`} />
          <input
            autoFocus={autoFocus}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder={isTracker
              ? t('officialStats.placeholders.searchPlayer', { defaultValue: 'Search a player…' })
              : t('home.searchMode.platformPlaceholder', { defaultValue: 'Search players, teams, tournaments…' })}
            className={`flex-1 min-w-0 bg-transparent text-white placeholder-gray-500 focus:outline-none ${
              isHero ? 'px-3 text-lg' : 'px-2 text-sm'
            }`}
          />
        </div>

        {/* Submit */}
        <button
          type="button"
          onClick={handleSearch}
          disabled={loading || (isTracker && !query.trim())}
          className={`flex-shrink-0 flex items-center justify-center gap-2 rounded-xl font-semibold text-zinc-900 bg-[#31C590] hover:bg-[#23F8B5] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 ${
            isHero ? 'px-6 text-base' : 'px-4 text-sm'
          }`}
        >
          {loading ? (
            <span className="w-5 h-5 border-2 border-zinc-900/40 border-t-zinc-900 rounded-full animate-spin" />
          ) : (
            <>
              <Search className="w-4 h-4" />
              <span className="hidden sm:inline">{t('common.searchAction', { defaultValue: 'Search' })}</span>
            </>
          )}
        </button>
      </div>

      {isHero && (
        <p className="mt-4 text-xs text-gray-400 text-center">
          {isTracker
            ? t('home.searchHint', { defaultValue: 'Search by gamer-tag on Steam, PlayStation or Xbox.' })
            : t('home.searchHintPlatform', { defaultValue: 'Find players, teams and tournaments across the platform.' })}
        </p>
      )}

      {/* Platform-mode results reuse the site-wide search overlay */}
      <SearchModal
        isOpen={siteModalOpen}
        onClose={() => setSiteModalOpen(false)}
        initialQuery={query}
      />
    </div>
  );
};

export default PlayerSearchBar;
